import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  signup: (data) => api.post('/auth/signup', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/me', data),
};

// Categories
export const categoryAPI = {
  getAll: () => api.get('/categories'),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Currency
export const currencyAPI = {
  getSupportedCurrencies: () => api.get('/currencies'),
  getExchangeRate: (from, to) => api.get('/exchange-rate', { params: { from_currency: from, to_currency: to } }),
};

// Expenses
export const expenseAPI = {
  getAll: (params) => api.get('/expenses', { params }),
  getSummary: (params) => api.get('/expenses/summary', { params }),
  create: (data) => api.post('/expenses', data),
  createFromText: (text) => api.post('/expenses/text', { text }),
  createFromReceipt: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/expenses/receipt', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000, // 60 second timeout for large images
    });
  },
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
  export: (year, month) => api.get('/expenses/export', {
    params: { year, month },
    responseType: 'blob',
  }),
  getReceipt: (id) => api.get(`/expenses/${id}/receipt`),
};

// Admin
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  getUserExpenses: (userId) => api.get(`/admin/users/${userId}/expenses`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  getDefaultCategories: () => api.get('/admin/categories'),
  createDefaultCategory: (data) => api.post('/admin/categories', data),
};

// Subscription
export const subscriptionAPI = {
  getStatus: () => api.get('/subscription'),
  getPlans: () => api.get('/subscription/plans'),
  createCheckout: (planId, originUrl) => api.post('/subscription/checkout', { plan_id: planId, origin_url: originUrl }),
  createPortal: () => api.post('/subscription/portal'),
};

export default api;
